var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { Tool } from '../Tool';
import { ToggleWindow } from '../../../components/map/tool/ToggleWindow';
/**
 * Fenêtre de recherche avec autocomplete
 */
let SearchWindow = class SearchWindow extends ToggleWindow {
    constructor() {
        super(...arguments);
        this.q = '';
        this.isSearching = false;
        this.suggestions = [];
        this.currentAbortController = null;
    }
    async handleSearchChange(e) {
        this.q = e.detail;
        if (this.q.trim() === '') {
            this.suggestions = [];
            return;
        }
        if (this.searchTimeout)
            clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(async () => {
            if (this.currentAbortController)
                this.currentAbortController.abort();
            this.currentAbortController = new AbortController();
            this.isSearching = true;
            try {
                this.suggestions = await this.tool.searchFunction(this.q, { signal: this.currentAbortController.signal });
            }
            catch (err) {
                if (err.name === 'AbortError')
                    return;
                console.error(err);
                this.suggestions = [];
            }
            finally {
                this.isSearching = false;
            }
        }, 300);
    }
    selectSuggestion(result) {
        this.q = result.address.fullAddress || '';
        this.suggestions = [];
        this.tool.handleSearch(this.q, result);
    }
    close() {
        this.tool.close();
    }
    render() {
        return html `
      <div class="container">
        <div class="input-row">
          <search-bar
            placeholder="Adresse, lieu, ville..."
            size="small"
            @search-change=${this.handleSearchChange}
          ></search-bar>

          <simple-button
            size="small"
            variant="danger"
            label="x"
            @button-click=${this.close}
          ></simple-button>
        </div>

        ${this.suggestions.length > 0
            ? html `
              <ul class="suggestions">
                ${this.suggestions.map((s) => html `<li @click=${() => this.selectSuggestion(s)}>
                    ${s.address.fullAddress || s.address.city || ''}
                  </li>`)}
              </ul>
            `
            : ''}
      </div>
    `;
    }
};
SearchWindow.styles = css `
    :host { display: block; width: 100%; }
    .container { display: flex; flex-direction: column; width: 100%; box-sizing: border-box; }
    .input-row { display: flex; align-items: center; gap: 8px; width: 100%; }
    search-bar { flex: 1 1 auto; min-width: 0; width: 200px; }
    .suggestions { list-style: none; margin: 4px 0 0 0; padding: 0; border: 1px solid #ccc; background: white; max-height: 150px; overflow-y: auto; z-index: 10; position: relative; }
    .suggestions li { padding: 4px 8px; cursor: pointer; }
    .suggestions li:hover { background: #f0f0f0; }
  `;
__decorate([
    property({ type: Object })
], SearchWindow.prototype, "tool", void 0);
__decorate([
    property({ type: String })
], SearchWindow.prototype, "q", void 0);
__decorate([
    property({ type: Boolean })
], SearchWindow.prototype, "isSearching", void 0);
__decorate([
    property({ type: Array })
], SearchWindow.prototype, "suggestions", void 0);
SearchWindow = __decorate([
    customElement('search-window')
], SearchWindow);
export { SearchWindow };
/**
 * Classe abstraite pour tous les outils de recherche
 */
export class SearchTool extends Tool {
    constructor(name, searchFunction) {
        super(name, new URL('../../../assets/search.svg', import.meta.url).href);
        this.searchWindow = null;
        this.toolBox = null;
        this.searchFunction = searchFunction;
    }
    handleSearch(query, preselected) {
        if (!this.searchFunction) {
            console.error('No search function defined');
            return;
        }
        if (this.searchWindow)
            this.searchWindow.isSearching = true;
        const doSearch = preselected
            ? Promise.resolve([preselected])
            : this.searchFunction(query);
        doSearch
            .then((results) => {
            const result = results[0];
            if (result && this.mapInstance) {
                this.flyToResult(result);
            }
        })
            .finally(() => {
            if (this.searchWindow)
                this.searchWindow.isSearching = false;
        });
    }
    flyToResult(result) {
        if (!this.mapInstance)
            return;
        this.mapInstance.flyTo({
            center: [result.coords.longitude, result.coords.latitude],
            zoom: 15,
            duration: 2000,
        });
        if (this.mapLib) {
            if (this.marker)
                this.marker.remove();
            const pinSvg = `
        <svg viewBox="0 0 16 16" width="30" height="30" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 0C4.686 0 2 2.686 2 6c0 1.43.486 2.816 1.379 3.932L8 16l4.621-6.068C13.514 8.816 14 7.43 14 6c0-3.314-2.686-6-6-6z" fill="#e63213ff"/>
          <circle cx="8" cy="6" r="2" fill="#ffffff"/>
        </svg>
      `;
            const el = document.createElement('div');
            el.innerHTML = pinSvg;
            el.style.cursor = 'pointer';
            el.style.pointerEvents = 'auto';
            this.marker = new this.mapLib.Marker({ element: el, anchor: 'bottom' })
                .setLngLat([result.coords.longitude, result.coords.latitude])
                .addTo(this.mapInstance);
        }
    }
    execute(mapInstance, mapLib = null, toolBox = null) {
        this.toolBox = toolBox;
        this.mapInstance = mapInstance;
        this.mapLib = mapLib;
        this.searchWindow = new SearchWindow();
        this.searchWindow.tool = this;
        this.toolBox?.toggleWindow(this.searchWindow);
    }
    close() {
        if (this.marker)
            this.marker.remove();
        this.marker = null;
        this.mapInstance = null;
        this.toolBox?.closeToggleWindow();
        this.searchWindow = null;
        this.toolBox = null;
    }
}
//# sourceMappingURL=SearchTool.js.map